import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TenantDivisionListItemSchema } from '@/entities/tenants-divisions/api/generated/model';
import {
  getAvailableYears,
  getDivisionOptionsForYear,
  groupDivisionsByYear,
} from '@/entities/tenants-divisions/lib/divisions-by-year';
import { Button, SelectField, Text } from '@/shared/ui';
import type { UserDivisionEntry } from './schema';

type Props = {
  divisionItems: TenantDivisionListItemSchema[];
};

/**
 * useFormContext で参照する最小限の型。
 * UserCreateFormValues / UserEditFormValues の共通部分のみを扱う。
 */
type FormWithDivisions = { divisions: UserDivisionEntry[] };

/**
 * 年度ごとに 1 つの所属組織を登録する入力フィールド
 *
 * - 各行: 「{year}年度」ラベル + その年度の組織 Select + 削除ボタン
 * - 上部: 「前年度を追加」ボタン（現在の最小年度 - 1 を追加）
 * - 下部: 「次年度を追加」ボタン（現在の最大年度 + 1 を追加）
 * - 登録年度は連続範囲を維持する（虫食い禁止）。これは UI 側（追加ボタン候補・削除ボタン制御）と
 *   Zod スキーマの refine で二重に強制する。
 */
export const DivisionsByYearField = ({ divisionItems }: Props) => {
  const { t } = useTranslation();
  const { control, formState } = useFormContext<FormWithDivisions>();
  const { fields, prepend, append, remove } = useFieldArray<FormWithDivisions, 'divisions'>({
    control,
    name: 'divisions',
  });
  const divisions = useWatch({ control, name: 'divisions' }) ?? [];

  // React Compiler が自動メモ化するため useMemo は使用しない
  const divisionsByYear = groupDivisionsByYear(divisionItems);
  const availableYears = getAvailableYears(divisionItems);

  // 現在の年度配列（昇順）
  const sortedYears = divisions.map((d) => d.year).sort((a, b) => Number(a) - Number(b));
  const minYear = sortedYears[0];
  const maxYear = sortedYears[sortedYears.length - 1];

  // 前年度／次年度ボタンの候補年度
  const prevYearCandidate = minYear ? String(Number(minYear) - 1) : undefined;
  const nextYearCandidate = maxYear ? String(Number(maxYear) + 1) : undefined;
  const canAddPrev = !!prevYearCandidate && availableYears.includes(prevYearCandidate);
  const canAddNext = !!nextYearCandidate && availableYears.includes(nextYearCandidate);

  const handleAddPrev = () => {
    if (!canAddPrev || !prevYearCandidate) return;
    prepend({ year: prevYearCandidate, division_id: '' });
  };
  const handleAddNext = () => {
    if (!canAddNext || !nextYearCandidate) return;
    append({ year: nextYearCandidate, division_id: '' });
  };

  // fields の表示順は年度の昇順に揃える（prepend / append で内部配列順は維持される想定だが、
  // 既存ユーザーの初期値が昇順で渡される前提で表示する）
  const fieldsWithYear = fields.map((field, index) => ({
    field,
    index,
    year: divisions[index]?.year ?? '',
  }));
  const orderedFields = [...fieldsWithYear].sort((a, b) => Number(a.year) - Number(b.year));

  // divisions 配列全体のエラー（min / refine）
  const divisionsRootError =
    formState.errors.divisions?.root?.message ?? formState.errors.divisions?.message;

  /**
   * 削除ボタンの表示・無効化判定
   * - 両端の年度（最小年度・最大年度）のみ表示する。中間行は非表示（連続範囲を維持するため）
   * - 行が 1 つしか残っていない場合は disabled（API の min=1 制約）
   */
  const getRemoveButtonState = (
    year: string,
  ): { hidden: true } | { hidden: false; disabled: boolean; disabledReason?: string } => {
    const isEdge = year === minYear || year === maxYear;
    if (!isEdge) return { hidden: true };
    if (fields.length === 1) {
      return {
        hidden: false,
        disabled: true,
        disabledReason: t('user-management.users.divisions.cannot-remove-last'),
      };
    }
    return { hidden: false, disabled: false };
  };

  return (
    <div className="flex flex-col gap-2">
      <Text variant="label">{t('user-management.field.division')}</Text>

      {/* 前年度を追加 */}
      <div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleAddPrev}
          disabled={!canAddPrev}
          className="w-fit"
          tooltip={{
            content: t('user-management.users.divisions.no-prev-year'),
            enabled: !canAddPrev,
          }}
        >
          <PlusIcon className="h-4 w-4" />
          {t('user-management.users.divisions.add-prev-year')}
        </Button>
      </div>

      {/* 各年度の行 */}
      <div className="flex flex-col gap-3">
        {orderedFields.map(({ field, index, year }) => {
          const yearOptions = getDivisionOptionsForYear(divisionsByYear, year);
          const removeState = getRemoveButtonState(year);
          return (
            <div key={field.id} className="flex items-end gap-2">
              <div className="flex-1">
                <SelectField
                  control={control}
                  name={`divisions.${index}.division_id`}
                  label={t('common.unit.year-with-label', { year })}
                  options={yearOptions}
                  required
                  showSearch
                  size="full"
                />
              </div>
              {/* 削除ボタンの有無で行幅がブレないよう、ボタン領域（icon サイズ=h-8 w-8）を常に確保する */}
              <div className="size-8 shrink-0">
                {!removeState.hidden && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => remove(index)}
                    disabled={removeState.disabled}
                    aria-label={t('user-management.users.divisions.remove-year')}
                    tooltip={{
                      content: removeState.disabledReason ?? '',
                      enabled: removeState.disabled,
                    }}
                  >
                    <XMarkIcon />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* 次年度を追加 */}
      <div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleAddNext}
          disabled={!canAddNext}
          className="w-fit"
          tooltip={{
            content: t('user-management.users.divisions.no-next-year'),
            enabled: !canAddNext,
          }}
        >
          <PlusIcon className="h-4 w-4" />
          {t('user-management.users.divisions.add-next-year')}
        </Button>
      </div>

      {divisionsRootError && (
        <div role="alert">
          <Text variant="description" color="error">
            {divisionsRootError}
          </Text>
        </div>
      )}
    </div>
  );
};
